import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Col, Empty, Grid, Row, Select, Space, Spin, Tag, Typography, message } from 'antd';
import { FilePdfOutlined, FileProtectOutlined } from '@ant-design/icons';
import api from '../../../api/client';
import { createEmptyCotizacionFinal, normalizeFromApi } from '../exportacion/cotizacionFinalForm';
import ServiciosAcordadosVista from './serviciosAcordadosVista';

const { Title, Text } = Typography;

const selectPopupProps = {
  getPopupContainer: () => document.body,
  placement: 'bottomLeft',
};

const ServiciosAcordados = () => {
  const screens = Grid.useBreakpoint();
  const isNarrow = screens.md === false;

  const [clientes, setClientes] = useState([]);
  const [loadingClientes, setLoadingClientes] = useState(true);
  const [numeroCliente, setNumeroCliente] = useState(null);

  const [cotizaciones, setCotizaciones] = useState([]);
  const [loadingCotizaciones, setLoadingCotizaciones] = useState(false);

  const [cotIdSeleccionada, setCotIdSeleccionada] = useState(null);
  const [detalleCotizacion, setDetalleCotizacion] = useState(null);
  const [formFinal, setFormFinal] = useState(null);
  const [motivoSinServicios, setMotivoSinServicios] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);

  const clienteSeleccionado = clientes.find((c) => c.numeroCliente === numeroCliente) || null;

  useEffect(() => {
    (async () => {
      setLoadingClientes(true);
      try {
        const { data } = await api.get('/clientes', { params: { page: 1, limit: 500, soloActivos: true } });
        setClientes(data?.items || []);
      } catch (e) {
        message.error(e?.response?.data?.message || 'Error al cargar clientes');
      } finally {
        setLoadingClientes(false);
      }
    })();
  }, []);

  const cargarCotizaciones = useCallback(async (cliNum) => {
    setLoadingCotizaciones(true);
    setCotIdSeleccionada(null);
    setDetalleCotizacion(null);
    setFormFinal(null);
    setMotivoSinServicios(null);
    try {
      const { data } = await api.get('/cotizaciones', {
        params: { numeroCliente: cliNum, page: 1, limit: 100 },
      });
      setCotizaciones(data.items || []);
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al cargar cotizaciones');
      setCotizaciones([]);
    } finally {
      setLoadingCotizaciones(false);
    }
  }, []);

  const onClienteChange = (v) => {
    const cli = v ?? null;
    setNumeroCliente(cli);
    setCotIdSeleccionada(null);
    setDetalleCotizacion(null);
    setFormFinal(null);
    setMotivoSinServicios(null);
    if (cli != null) {
      cargarCotizaciones(cli);
    } else {
      setCotizaciones([]);
    }
  };

  const seleccionarCotizacion = useCallback(async (cot) => {
    setCotIdSeleccionada(cot.id);
    setLoadingDetalle(true);
    setDetalleCotizacion(null);
    setFormFinal(null);
    setMotivoSinServicios(null);
    try {
      const [cotRes, finRes] = await Promise.all([
        api.get(`/cotizaciones/${cot.id}`),
        api.get(`/cotizaciones/${cot.id}/cotizacion-final`).catch((e) => ({ error: e })),
      ]);

      const cotData = cotRes.data;
      setDetalleCotizacion(cotData);

      const defaultTipo = cotData.tipoOperacion === 'IMPORTACION' ? 'IMPORTACION' : 'EXPORTACION';
      const finError = finRes?.error;
      const finData = finRes?.data?.cotizacionFinal;

      if (finError) {
        const status = finError?.response?.status;
        if (status === 403) {
          setMotivoSinServicios('no_aprobada');
          setFormFinal(createEmptyCotizacionFinal({ tipoOperacion: defaultTipo }));
        } else if (status === 404) {
          setMotivoSinServicios('no_encontrada');
          setFormFinal(createEmptyCotizacionFinal({ tipoOperacion: defaultTipo }));
        } else {
          throw finError;
        }
      } else if (finData) {
        setFormFinal(normalizeFromApi(finData, defaultTipo));
      } else {
        setMotivoSinServicios('sin_guardar');
        setFormFinal(createEmptyCotizacionFinal({ tipoOperacion: defaultTipo }));
      }
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al cargar la cotización');
      setCotIdSeleccionada(null);
    } finally {
      setLoadingDetalle(false);
    }
  }, []);

  const etiquetaCotizacion = (cot) => {
    const nro = cot.numeroSecuencial ?? cot.id;
    const tipo = cot.tipoOperacion === 'IMPORTACION' ? 'Importación' : 'Exportación';
    return { nro, tipo };
  };

  const descargarPdf = useCallback(async () => {
    if (!cotIdSeleccionada || !detalleCotizacion) return;
    setLoadingPdf(true);
    try {
      const { data } = await api.get(`/cotizaciones/${cotIdSeleccionada}/servicios-acordados-pdf`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const nro = detalleCotizacion.numeroSecuencial ?? detalleCotizacion.id;
      const tag = detalleCotizacion.tipoOperacion === 'IMPORTACION' ? 'impo' : 'expo';
      a.download = `servicios-acordados-${tag}-${nro}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      let msg = 'Error al generar el PDF';
      try {
        const d = e?.response?.data;
        if (d instanceof Blob) {
          const txt = await d.text();
          const j = JSON.parse(txt);
          if (j?.message) msg = j.message;
        } else if (typeof d?.message === 'string') {
          msg = d.message;
        }
      } catch {
        /* ignore */
      }
      message.error(msg);
    } finally {
      setLoadingPdf(false);
    }
  }, [cotIdSeleccionada, detalleCotizacion]);

  return (
    <Card
      style={{ borderColor: '#d8e8dc', background: '#ffffff' }}
      styles={{ body: { padding: isNarrow ? 12 : 16 } }}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={4} style={{ margin: 0, color: '#198039' }}>
            <FileProtectOutlined style={{ marginRight: 8 }} />
            Servicios acordados
          </Title>
          <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            Elegí un cliente para ver sus cotizaciones y los servicios acordados de cada una.
          </Text>
        </div>

        <div style={{ maxWidth: 480 }}>
          <Text strong style={{ display: 'block', marginBottom: 6 }}>
            Cliente
          </Text>
          <Select
            showSearch
            allowClear
            loading={loadingClientes}
            placeholder="Seleccionar cliente"
            style={{ width: '100%' }}
            optionFilterProp="label"
            value={numeroCliente ?? undefined}
            onChange={onClienteChange}
            options={clientes.map((c) => ({
              value: c.numeroCliente,
              label: `${c.nombre} (Nº ${c.numeroCliente})`,
            }))}
            {...selectPopupProps}
          />
        </div>

        {numeroCliente != null ? (
          <Row gutter={[16, 16]} align="top">
            <Col xs={24} lg={8}>
              <Card
                size="small"
                title={clienteSeleccionado ? clienteSeleccionado.nombre : 'Cotizaciones'}
                style={{ borderColor: '#d8e8dc' }}
                loading={loadingCotizaciones}
              >
                {cotizaciones.length === 0 && !loadingCotizaciones ? (
                  <Empty description="Sin cotizaciones para este cliente" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    {cotizaciones.map((cot) => {
                      const { nro, tipo } = etiquetaCotizacion(cot);
                      const activa = cotIdSeleccionada === cot.id;
                      return (
                        <Card
                          key={cot.id}
                          size="small"
                          hoverable
                          onClick={() => seleccionarCotizacion(cot)}
                          style={{
                            borderColor: activa ? '#198039' : '#d8e8dc',
                            background: activa ? '#f0faf2' : '#fff',
                            cursor: 'pointer',
                          }}
                        >
                          <Space wrap>
                            <Text strong>Cotización {nro}</Text>
                            <Tag color={cot.tipoOperacion === 'IMPORTACION' ? 'blue' : 'green'}>{tipo}</Tag>
                            {cot.tieneCotizacionFinal ? (
                              <Tag color="success">Con servicios</Tag>
                            ) : (
                              <Tag>Sin servicios</Tag>
                            )}
                          </Space>
                        </Card>
                      );
                    })}
                  </Space>
                )}
              </Card>
            </Col>

            <Col xs={24} lg={16}>
              {!cotIdSeleccionada ? (
                <Card size="small" style={{ borderColor: '#d8e8dc' }}>
                  <Empty
                    description="Seleccioná una cotización para ver productos y servicios acordados"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                </Card>
              ) : (
                <Spin spinning={loadingDetalle}>
                  {detalleCotizacion && formFinal ? (
                    <div>
                      <Space
                        align="center"
                        style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}
                        wrap
                      >
                        <Title level={5} style={{ margin: 0, color: '#198039' }}>
                          Cotización {detalleCotizacion.numeroSecuencial ?? detalleCotizacion.id}
                        </Title>
                        <Button
                          type="primary"
                          icon={<FilePdfOutlined />}
                          loading={loadingPdf}
                          onClick={() => void descargarPdf()}
                        >
                          Exportar PDF
                        </Button>
                      </Space>
                      <ServiciosAcordadosVista
                        form={formFinal}
                        productos={detalleCotizacion.productos || []}
                        motivoSinServicios={motivoSinServicios}
                      />
                    </div>
                  ) : null}
                </Spin>
              )}
            </Col>
          </Row>
        ) : null}
      </Space>
    </Card>
  );
};

export default ServiciosAcordados;
